import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import iconATM from "../assets/icons/payment/ATM.png";
import iconPayment from "../assets/icons/payment/money 1.png";
import { Image } from "antd";
import {
  useCreateQRPaymentMutation,
  useCancelQRPaymentMutation,
} from "../store/mutations/payment/payment.mutate";
import { checkPaymentStatus } from "../apis/payment/payment";
import { StateContext } from "../context/reducer";
import {
  PaymentMethodCard,
  PaymentInput,
} from "../components/Payment";
import PopupError from "../components/PopupConfirm/PopupError";
import PopupConfirm from "../components/PopupConfirm/PopupConfirm";

const PaymentPage = () => {
  const {
    state: { user },
  } = useContext(StateContext);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [amount, setAmount] = useState("");
  const [isQRModalVisible, setIsQRModalVisible] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [countdown, setCountdown] = useState(600); // 10 minutes countdown
  const pollingIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const net = localStorage.getItem("net");
  const netData = net ? JSON.parse(net) : "";
  const netId = netData.id ? netData.id : "0";

  const { mutateAsync: createQRPaymentMutation, isPending: isCreatingQR } =
    useCreateQRPaymentMutation();
  const { mutateAsync: cancelQRPaymentMutation } = useCancelQRPaymentMutation();

  const handleMethodClick = (method) => {
    setSelectedMethod(selectedMethod === method ? null : method);
  };

  const handleAmountChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (!value) {
      setAmount("");
      return;
    }
    let amountNumber = parseInt(value, 10);
    if (amountNumber > 10000000) {
      amountNumber = 10000000;
    }
    setAmount(amountNumber);
  };

  // Format number with dots for display
  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Poll for payment status
  const startPolling = useCallback((orderId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await checkPaymentStatus(orderId);
        if (response.success && response.data?.isPaid) {
          // Payment successful!
          stopPolling();
          setIsQRModalVisible(false);
          setIsSuccessPopupVisible(true);
          setPaymentData(null);
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
      }
    }, 3000); // Poll every 3 seconds
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start countdown timer
  const startCountdown = useCallback(() => {
    setCountdown(600); // Reset to 10 minutes
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time's up - cancel payment
          handleCancelPayment();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const handleConfirmPaymentClick = async () => {
    if (selectedMethod === "BankQR" && amount) {
      try {
        const amountNumber =
          typeof amount === "string"
            ? parseInt(amount.replace(/\./g, ""), 10)
            : amount;

        if (isNaN(amountNumber)) {
          setIsErrorPopupVisible(true);
          setErrorPopupMessage("Số tiền không hợp lệ.");
          return;
        }
        if (amountNumber < 20000) {
          setIsErrorPopupVisible(true);
          setErrorPopupMessage("Số tiền nạp tối thiểu là 20,000 VND");
          return;
        }

        const response = await createQRPaymentMutation({
          amount: amountNumber,
          orderInfo: "nap tien",
          userId: user?.id,
          netId: netId,
        });

        if (response.success && response.data) {
          setPaymentData(response.data);
          setIsQRModalVisible(true);
          startPolling(response.data.orderId);
          startCountdown();
        } else {
          setIsErrorPopupVisible(true);
          setErrorPopupMessage(
            response.message || "Không thể tạo đơn thanh toán."
          );
        }
      } catch (error) {
        console.error("Error creating payment:", error);
        setIsErrorPopupVisible(true);
        setErrorPopupMessage(
          "Đã có lỗi xảy ra, liên hệ hỗ trợ để được giúp đỡ."
        );
      }
    }
  };

  const handleCancelPayment = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    stopPolling();

    try {
      if (paymentData?.orderId) {
        await cancelQRPaymentMutation({
          orderId: paymentData.orderId,
          reason: "User cancelled",
        });
      }
      setIsQRModalVisible(false);
      setPaymentData(null);
    } catch (error) {
      console.error("Error cancelling payment:", error);
      setIsErrorPopupVisible(true);
      setErrorPopupMessage("Đã có lỗi xảy ra khi hủy thanh toán.");
    } finally {
      setIsCancelling(false);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could show a toast here
      alert(`Đã sao chép ${label}`);
    });
  };

  return (
    <div className="flex justify-center h-full max-h-screen overflow-y-auto pt-[92px]">
      <div className="mt-10 w-[965px] h-[550px] overflow-y-auto bg-[#242333] rounded-lg p-6">
        <div className="flex justify-center items-center">
          <div className="h-[65px] w-[65px] mr-[12px]">
            <Image src={iconPayment} alt="Payment" preview={false} />
          </div>
          <h2 className="text-2xl font-bold pt-2 text-white">Nạp tiền</h2>
        </div>
        <div className="mt-6 space-y-4">
          <PaymentMethodCard
            method="BankQR"
            icon={iconATM}
            title="Thanh toán tự động quét mã QR (số tiền nạp tối thiểu 20,000 VND)"
            description="Quét mã QR và chuyển khoản qua ứng dụng ngân hàng (Mobile Banking) - Xử lý tự động trong 30 giây, phí GD 0%"
            isSelected={selectedMethod === "BankQR"}
            onClick={() => handleMethodClick("BankQR")}
          >
            <PaymentInput
              amount={amount}
              isLoading={isCreatingQR}
              onAmountChange={handleAmountChange}
              onConfirmClick={handleConfirmPaymentClick}
            />
          </PaymentMethodCard>
        </div>
      </div>

      {/* QR Payment Modal */}
      {isQRModalVisible && paymentData && (
        <div className="fixed h-full inset-0 z-50 top-[27px] flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#1a1a2e] rounded-xl shadow-2xl mb-[2rem] w-[500px] max-w-[95%] border border-[#3a3a5a]">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b border-[#3a3a5a]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xl">💳</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Thanh toán chuyển khoản
                  </h2>
                  <p className="text-sm text-gray-400">
                    Quét mã QR để thanh toán
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancelPayment}
                className="text-gray-400 hover:text-white text-2xl transition-colors"
                disabled={isCancelling}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-5 h-[25rem] overflow-y-auto">
              {/* Countdown Timer */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full">
                  <span className="animate-pulse">⏱️</span>
                  <span className="font-mono font-bold">
                    {formatTime(countdown)}
                  </span>
                  <span className="text-sm">còn lại</span>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-5">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <img
                    src={paymentData.qrCode}
                    alt="QR Code"
                    className="w-[300px] h-[300px] object-contain"
                  />
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-[#242448] rounded-xl p-4 space-y-3">
                {/* Bank Name */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Ngân hàng</span>
                  <span className="text-white font-medium">
                    {paymentData.bankName}
                  </span>
                </div>

                {/* Account Number */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Số tài khoản</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-mono font-bold text-lg">
                      {paymentData.accountNo}
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(paymentData.accountNo, "số tài khoản")
                      }
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      📋
                    </button>
                  </div>
                </div>

                {/* Account Name */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Chủ tài khoản</span>
                  <span className="text-white font-medium">
                    {paymentData.accountName}
                  </span>
                </div>

                {/* Amount */}
                <div className="flex justify-between items-center bg-green-500/20 p-3 rounded-lg -mx-1">
                  <span className="text-green-400 font-medium">Số tiền</span>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400 font-bold text-xl">
                      {formatNumber(paymentData.amount)} VND
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(paymentData.amount, "số tiền")
                      }
                      className="text-green-300 hover:text-green-200 text-sm"
                    >
                      📋
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="flex justify-between items-center bg-blue-500/20 p-3 rounded-lg -mx-1">
                  <span className="text-blue-400 font-medium">Nội dung CK</span>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-400 font-mono font-bold">
                      {paymentData.description}
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(paymentData.description, "nội dung")
                      }
                      className="text-blue-300 hover:text-blue-200 text-sm"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center">
                  ⚠️ <strong>Lưu ý quan trọng:</strong> Vui lòng nhập{" "}
                  <strong>ĐÚNG NỘI DUNG</strong> chuyển khoản để hệ thống tự
                  động xác nhận thanh toán
                </p>
              </div>

              {/* Status */}
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm">
                    Đang chờ thanh toán... Hệ thống sẽ tự động cập nhật
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-center p-4 border-t border-[#3a3a5a]">
              <button
                onClick={handleCancelPayment}
                disabled={isCancelling}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                  isCancelling
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50"
                }`}
              >
                {isCancelling ? "Đang hủy..." : "Hủy thanh toán"}
              </button>
            </div>
          </div>
        </div>
      )}

      <PopupConfirm
        visible={isSuccessPopupVisible}
        onClose={() => setIsSuccessPopupVisible(false)}
        title={"Thành công!"}
        content={"Thanh toán của bạn đã được xử lý thành công. Số dư đã được cộng vào tài khoản."}
        buttons={[
          {
            label: "Đóng",
            className:
              "px-4 py-2 text-sm font-medium text-white bg-[#4CAF50] border border-gray-300 rounded-[30px] shadow-sm opacity-70 hover:opacity-100",
            onClick: () => setIsSuccessPopupVisible(false),
          },
        ]}
      />
      <PopupError
        visible={isErrorPopupVisible}
        onClose={() => setIsErrorPopupVisible(false)}
        title={"Cảnh báo!"}
        content={errorPopupMessage}
        buttons={[
          {
            label: "Đóng",
            className:
              "px-4 py-2 text-sm font-medium text-white bg-[#FF6D6D] border border-gray-300 rounded-[30px] shadow-sm opacity-70 hover:opacity-100",
            onClick: () => setIsErrorPopupVisible(false),
          },
        ]}
      />
    </div>
  );
};

export default PaymentPage;
